import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Wallet, ArrowRight, CheckCircle2, Clock, AlertCircle, ExternalLink,
  DollarSign, Loader2, ShieldCheck, Zap, ArrowUpRight, Building2,
} from 'lucide-react';

function money(n) {
  return `$${Number(n || 0).toFixed(2)}`;
}

function StatusBadge({ status }) {
  const cfg = {
    not_started: { icon: AlertCircle, label: 'Not Set Up', className: 'bg-muted text-muted-foreground border-border' },
    pending: { icon: Clock, label: 'Pending', className: 'bg-amber-500/15 text-amber-400 border-amber-500/30' },
    onboarding: { icon: Clock, label: 'In Progress', className: 'bg-amber-500/15 text-amber-400 border-amber-500/30' },
    enabled: { icon: CheckCircle2, label: 'Payouts Enabled', className: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' },
  }[status] || { icon: AlertCircle, label: status, className: 'bg-muted' };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-black uppercase tracking-widest ${cfg.className}`} data-testid={`connect-status-${status}`}>
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}

function CashoutRow({ p }) {
  const statusColor = {
    pending: 'text-amber-400 bg-amber-500/10 border-amber-500/30',
    approved: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30',
    rejected: 'text-rose-400 bg-rose-500/10 border-rose-500/30',
  }[p.status] || 'text-muted-foreground';
  return (
    <div className="flex items-center justify-between p-3 rounded-xl bg-card/40 border border-white/5" data-testid={`payout-row-${p.request_id}`}>
      <div>
        <p className="font-black text-sm">{money(p.amount_usd)}</p>
        <p className="text-[11px] text-muted-foreground">
          {new Date(p.created_at).toLocaleDateString()} · via {p.method || 'stripe'}
        </p>
      </div>
      <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase border ${statusColor}`}>
        {p.status}
      </span>
    </div>
  );
}

export default function PayoutSettingsPage() {
  const { getAuthHeaders, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [status, setStatus] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const [s, h] = await Promise.all([
        fetch(`${API}/payouts/connect/status`, { headers }).then(r => r.ok ? r.json() : null),
        fetch(`${API}/payouts/my-history`, { headers }).then(r => r.ok ? r.json() : null),
      ]);
      setStatus(s);
      setPayouts(h?.payouts || []);
    } catch { /* noop */ }
    setLoading(false);
  }, [getAuthHeaders]);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) { navigate('/login'); return; }
    loadAll();
    if (params.get('connect') === 'success') {
      toast.success('Onboarding submitted! Stripe is verifying your info.');
    }
  }, [authLoading, isAuthenticated, loadAll, navigate, params]);

  const startOnboarding = async () => {
    setStarting(true);
    try {
      const res = await fetch(`${API}/payouts/connect/onboarding`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not start onboarding');
        setStarting(false);
        return;
      }
      const data = await res.json();
      window.location.href = data.url;
    } catch {
      toast.error('Could not start onboarding');
      setStarting(false);
    }
  };

  const refreshLink = async () => {
    setStarting(true);
    try {
      const res = await fetch(`${API}/payouts/connect/refresh-link`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      window.location.href = data.url;
    } catch {
      toast.error('Could not create link');
      setStarting(false);
    }
  };

  const openStripeDashboard = async () => {
    try {
      const res = await fetch(`${API}/payouts/connect/login-link`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      window.open(data.url, '_blank');
    } catch {
      toast.error('Could not open Stripe dashboard');
    }
  };

  if (loading || !status) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 space-y-4">
          <Skeleton className="h-40 rounded-2xl" />
          <Skeleton className="h-64 rounded-2xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Hero */}
      <div className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/15 via-teal-500/10 to-cyan-500/10" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 py-10">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg">
              <Wallet className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-black tracking-tight" data-testid="payouts-title">Payouts</h1>
              <p className="text-sm text-muted-foreground">Get paid to your bank via Stripe</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {/* Status card */}
        <Card className="p-6" data-testid="connect-status-card">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-[10px] uppercase font-black tracking-widest text-muted-foreground">Stripe Connect</p>
                <h2 className="text-xl font-black">Your Payout Account</h2>
              </div>
            </div>
            <StatusBadge status={status.status} />
          </div>

          {status.status === 'not_started' && (
            <div className="space-y-4">
              <div className="p-4 rounded-xl bg-muted/30 border border-white/5">
                <div className="flex items-start gap-2">
                  <ShieldCheck className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-semibold">Set up Stripe payouts to receive your earnings.</p>
                    <p className="text-muted-foreground text-xs mt-1">
                      Takes about 3 minutes. You&apos;ll provide your bank details securely through Stripe.
                      DMV Soundstage never sees your bank info.
                    </p>
                  </div>
                </div>
              </div>
              <Button
                onClick={startOnboarding}
                disabled={starting}
                size="lg"
                className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white shadow-lg"
                data-testid="start-connect-onboarding"
              >
                {starting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirecting…</>
                ) : (
                  <>Set Up Payouts on Stripe <ArrowRight className="w-4 h-4 ml-1" /></>
                )}
              </Button>
            </div>
          )}

          {(status.status === 'pending' || status.status === 'onboarding') && (
            <div className="space-y-3">
              <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/25">
                <p className="font-semibold text-sm mb-1">Onboarding not complete yet</p>
                <p className="text-xs text-muted-foreground">
                  Stripe needs a few more details before payouts can go live.
                  {status.requirements_currently_due?.length > 0 && (
                    <> Missing: <span className="font-mono text-amber-400">{status.requirements_currently_due.slice(0, 5).join(', ')}</span></>
                  )}
                </p>
              </div>
              <Button
                onClick={refreshLink}
                disabled={starting}
                className="w-full bg-amber-500 hover:bg-amber-600 text-white"
                data-testid="continue-connect-onboarding"
              >
                {starting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Continue on Stripe <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          )}

          {status.status === 'enabled' && (
            <div className="space-y-3">
              <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center gap-3">
                <CheckCircle2 className="w-6 h-6 text-emerald-400" />
                <div>
                  <p className="font-black">You&apos;re all set!</p>
                  <p className="text-xs text-muted-foreground">Payouts land in your bank within 2-3 business days after approval.</p>
                </div>
              </div>
              <Button
                variant="outline"
                onClick={openStripeDashboard}
                className="w-full"
                data-testid="open-stripe-dashboard"
              >
                Open Stripe Dashboard <ExternalLink className="w-3 h-3 ml-2" />
              </Button>
            </div>
          )}
        </Card>

        {/* History */}
        <Card className="p-6" data-testid="payouts-history">
          <div className="flex items-center gap-2 mb-4">
            <ArrowUpRight className="w-5 h-5 text-emerald-400" />
            <h3 className="font-black uppercase tracking-tight">Payout History</h3>
          </div>
          {payouts.length === 0 ? (
            <div className="text-center py-8 text-sm text-muted-foreground" data-testid="payouts-empty">
              <DollarSign className="w-8 h-8 mx-auto opacity-40 mb-2" />
              <p>No payouts yet. Once you request a cashout, it will appear here.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {payouts.map(p => <CashoutRow key={p.request_id} p={p} />)}
            </div>
          )}
        </Card>

        <div className="text-center">
          <Button variant="link" onClick={() => navigate('/dashboard')}>
            <Zap className="w-3 h-3 mr-1" /> Back to Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
}
